import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { StandardURL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faCreditCard, 
    faPlus, 
    faTrashAlt, 
    faPencilAlt, 
    faSyncAlt, 
    faCheck,
    faTimes
} from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";

const CardPaymentSettings = () => {
    // State for data and loading
    const [settings, setSettings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // State for Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [settingToDelete, setSettingToDelete] = useState(null); // { propertyId: string }

    // State for Forms (used in both Add/Update)
    const [formRate, setFormRate] = useState("");
    const [formPropertyId, setFormPropertyId] = useState(""); // Only used in Add Modal
    const [isSubmitting, setIsSubmitting] = useState(false);

    const token = localStorage.getItem("token");

    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    };

    // --- Data Fetching: View Multiple Records ---
    const fetchSettings = async () => {
        if (!token) {
            toast.error("Authentication token is missing.");
            return;
        }

        setIsLoading(true);
        try {
            // GET: https://backend.inyumba.co.ke/iNyumba/user/cardvalidation/viewcard
            const response = await axios.get(
                `${StandardURL}/iNyumba/user/cardvalidation/viewcard`,
                { headers }
            );

            // Assuming response.data.response is an array of records
            if (response.data && Array.isArray(response.data.response)) {
                setSettings(response.data.response);
                toast.success("Card payment settings loaded successfully.");
            } else {
                 // Handle cases where response is not an array (e.g., empty object or no settings)
                setSettings([]);
                toast.info("No card validation settings configured.");
            }
        } catch (error) {
            setSettings([]);
            const errorMessage = error.response?.data?.response || "Failed to fetch settings.";
            toast.error(errorMessage);
            console.error("Error fetching card settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    // --- Action Handlers ---

    // 1. Create (Add)
    const handleCreate = async (e) => {
        e.preventDefault();

        const rateValue = parseFloat(formRate);

        if (!formPropertyId) {
            toast.error("Property ID is required.");
            return;
        }
        if (isNaN(rateValue) || rateValue <= 0) {
            toast.error("Please enter a valid rate (must be a positive number).");
            return;
        }

        setIsSubmitting(true);
        const payload = {
            propertyId: formPropertyId,
            rate: formRate,
        };

        try {
            // POST: https://backend.inyumba.co.ke/iNyumba/user/cardvalidation/create
            const response = await axios.post(
                `${StandardURL}/iNyumba/user/cardvalidation/create`,
                payload,
                { headers }
            );

            if (response.data.success) {
                toast.success(`Setting created for Property ID: ${formPropertyId}`);
                setShowAddModal(false);
                setFormRate("");
                setFormPropertyId("");
                await fetchSettings(); // Refresh the list
            } else {
                toast.error(response.data.response || "Failed to create setting.");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.response || "An error occurred while creating the setting.";
            toast.error(errorMessage);
            console.error("POST error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 2. Update Rate (Triggered from the table row)
    const handleUpdate = async (propertyId, newRate) => {
        const rateValue = parseFloat(newRate);

        if (isNaN(rateValue) || rateValue <= 0) {
            toast.error("Invalid rate value. Update failed.");
            return;
        }

        // Optimistic update for UX, handle loading state on the row itself if necessary
        // For simplicity here, we'll use isSubmitting to block general actions.
        setIsSubmitting(true); 

        try {
            // PATCH: https://backend.inyumba.co.ke/iNyumba/user/cardvalidation/update/{propertyId}
            const response = await axios.patch(
                `${StandardURL}/iNyumba/user/cardvalidation/update/${propertyId}`,
                { rate: newRate },
                { headers }
            );

            if (response.data.success) {
                toast.success(`Rate updated for Property ID: ${propertyId}`);
                await fetchSettings(); // Refresh the list
            } else {
                toast.error(response.data.response || `Failed to update rate for ${propertyId}.`);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.response || "An error occurred during the update.";
            toast.error(errorMessage);
            console.error("PATCH error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 3. Delete
    const handleDelete = async () => {
        if (!settingToDelete) return;

        const { propertyId } = settingToDelete;
        setIsSubmitting(true);
        setShowDeleteModal(false);

        try {
            // DELETE: https://backend.inyumba.co.ke/iNyumba/user/cardvalidation/delete/{propertyId}
            const response = await axios.delete(
                `${StandardURL}/iNyumba/user/cardvalidation/delete/${propertyId}`,
                { headers }
            );

            if (response.data.success) {
                toast.success(`Setting deleted for Property ID: ${propertyId}.`);
                await fetchSettings(); // Refresh the list
            } else {
                toast.error(response.data.response || `Failed to delete setting for ${propertyId}.`);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.response || "An error occurred during deletion.";
            toast.error(errorMessage);
            console.error("DELETE error:", error);
        } finally {
            setIsSubmitting(false);
            setSettingToDelete(null);
        }
    };


    // --- Helper Component for Updatable Row ---
    const SettingRow = ({ setting }) => {
        const [editRate, setEditRate] = useState(setting.rate);
        const [isEditing, setIsEditing] = useState(false);

        const handleSave = () => {
            if (isSubmitting) return;
            // Call the main update handler
            handleUpdate(setting.propertyId, editRate);
            setIsEditing(false);
        };
        
        // Reset local rate if the global settings list is updated (e.g., after successful fetch)
        useEffect(() => {
            setEditRate(setting.rate);
        }, [setting.rate]);

        return (
            <tr className="border-b hover:bg-gray-50 transition duration-150">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{setting.propertyId}</td>
                <td className="px-4 py-3">
                    {isEditing ? (
                        <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={editRate}
                            onChange={(e) => setEditRate(e.target.value)}
                            className="w-24 border border-indigo-300 rounded-md p-1 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={isSubmitting}
                        />
                    ) : (
                        <span className="text-gray-700">{setting.rate}</span>
                    )}
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium space-x-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting || parseFloat(editRate) === parseFloat(setting.rate)}
                                className={`text-white p-2 rounded-full transition duration-150 ${
                                    isSubmitting || parseFloat(editRate) === parseFloat(setting.rate)
                                        ? "bg-gray-400"
                                        : "bg-green-600 hover:bg-green-700"
                                }`}
                                title="Save"
                            >
                                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditRate(setting.rate); // Reset to original rate
                                }}
                                disabled={isSubmitting}
                                className="text-white p-2 bg-red-500 rounded-full hover:bg-red-600 transition duration-150"
                                title="Cancel"
                            >
                                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setIsEditing(true)}
                                disabled={isSubmitting || isLoading}
                                className="text-white p-2 bg-indigo-600 rounded-full hover:bg-indigo-700 transition duration-150"
                                title="Edit Rate"
                            >
                                <FontAwesomeIcon icon={faPencilAlt} className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => {
                                    setSettingToDelete(setting);
                                    setShowDeleteModal(true);
                                }}
                                disabled={isSubmitting || isLoading}
                                className="text-white p-2 bg-red-600 rounded-full hover:bg-red-700 transition duration-150"
                                title="Delete"
                            >
                                <FontAwesomeIcon icon={faTrashAlt} className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </td>
            </tr>
        );
    };

    // --- Main Component Render ---
    return (
        <EmailGuard>
            <AuthGuard>
                <div className="p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
                    <div className="max-w-4xl mx-auto bg-white p-6 rounded-xl shadow-2xl space-y-6">
                        
                        {/* Header and Controls */}
                        <div className="flex justify-between items-center border-b pb-4">
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-indigo-600" />
                                Card Payment Validation Settings
                            </h1>
                            <div className="flex space-x-3">
                                <button
                                    onClick={fetchSettings}
                                    disabled={isLoading || isSubmitting}
                                    className="p-2 text-gray-500 hover:text-indigo-600 transition border rounded-full"
                                    title="Refresh Settings"
                                >
                                    <FontAwesomeIcon icon={faSyncAlt} spin={isLoading} className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => {
                                        setFormRate("");
                                        setFormPropertyId("");
                                        setShowAddModal(true);
                                    }}
                                    disabled={isSubmitting || isLoading}
                                    className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition flex items-center"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add New Setting
                                </button>
                            </div>
                        </div>

                        {/* Settings Table */}
                        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Property ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Rate (%)
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="3" className="text-center py-6">
                                                <Oval height={30} width={30} color="#4F46E5" secondaryColor="transparent" strokeWidth={5} visible={true} ariaLabel="loading-indicator" />
                                                <span className="ml-2 text-gray-600">Loading settings...</span>
                                            </td>
                                        </tr>
                                    ) : settings.length > 0 ? (
                                        settings.map((setting) => (
                                            <SettingRow key={setting.propertyId} setting={setting} />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="text-center py-6 text-gray-500">
                                                No card payment settings found. Click "Add New Setting" to create one.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* --- Modals --- */}
                
                {/* 1. Add New Setting Modal */}
                {showAddModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 transition duration-300">
                        <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
                            <h2 className="text-xl font-bold text-indigo-600 mb-4 flex items-center">
                                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add New Card Validation Setting
                            </h2>
                            <form onSubmit={handleCreate} className="space-y-4">
                                <div>
                                    <label htmlFor="addPropertyId" className="block text-sm font-medium text-gray-700">Property ID</label>
                                    <input
                                        id="addPropertyId"
                                        type="text"
                                        required
                                        value={formPropertyId}
                                        onChange={(e) => setFormPropertyId(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Enter the property ID"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="addRate" className="block text-sm font-medium text-gray-700">Validation Rate (%)</label>
                                    <input
                                        id="addRate"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        required
                                        value={formRate}
                                        onChange={(e) => setFormRate(e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="e.g., 5.00 for 5%"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="flex justify-end space-x-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddModal(false)}
                                        className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-lg hover:bg-gray-400 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`px-4 py-2 text-white font-semibold rounded-lg transition ${
                                            isSubmitting ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                                        }`}
                                    >
                                        {isSubmitting ? "Adding..." : "Add Setting"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* 2. Delete Confirmation Modal */}
                {showDeleteModal && settingToDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                        <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-sm">
                            <h2 className="text-xl font-bold text-red-600 mb-4">Confirm Deletion</h2>
                            <p className="text-gray-700 mb-6">
                                Are you sure you want to delete the setting for **Property ID: {settingToDelete.propertyId}**? This action cannot be undone.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSettingToDelete(null);
                                    }}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-lg hover:bg-gray-400 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isSubmitting}
                                    className={`px-4 py-2 text-white font-semibold rounded-lg transition ${
                                        isSubmitting ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                                    }`}
                                >
                                    {isSubmitting ? "Deleting..." : "Yes, Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

export default CardPaymentSettings;